import React, { Component, Fragment } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import he from 'he';
import { compose, filter, sortWith, ascend, descend, prop, head, last, defaultTo, path, find } from 'ramda';
import moment from 'moment';

import { routes } from '../../../index';
import magic from '../../../../util/magic';

import { apiDeleteCertificationPayment } from '../../../../api/certification';
import { handleSuccessBase, handleErrorBase } from '../../../../util/forms';

import UserCertificationModal from '../../../../components/Modal/UserCertificationModal';
import ReinstateCertificationModal from '../../../../components/Modal/ReinstateCertificationModal';
import CertificationPaymentModal from '../../../../components/Modal/CertificationPaymentModal';
import Card from '../../../../components/Card';
import FinalPrice from '../Registration/Steps/Components/FinalPrice';
import CEUListCard from './Components/CEUListCard';
import { getCertificationExtension } from '../../../../util/helpers';

const mapStore = ({ certifications }) => ({
  certs: certifications,
});

class Certification extends Component {
  static propTypes = {
    certs: PropTypes.array,
    certificationPayments: PropTypes.array,
    certifications: PropTypes.array,
    creditTypes: PropTypes.array,
    extensions: PropTypes.array,
    updateUser: PropTypes.func,
    updateUserPayments: PropTypes.func,
    user: PropTypes.object,
  };

  state = {
    showEditModal: false,
    showPaymentModal: false,
    showReinstatementModal: false,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { certifications, match, extensions, certs } = nextProps;
    const selectedCertificationId = match.params.id;
    const userCertifications = compose(
      sortWith([ascend(prop('reportingPeriodId')), descend(prop('status'))]),
      filter((c) => c.certificationId == selectedCertificationId),
    )(certifications);
    const cert = defaultTo(
      {},
      find((c) => c.id == selectedCertificationId, certs),
    );
    const currentCertification = defaultTo({}, last(userCertifications));
    const firstCertification = defaultTo({}, head(userCertifications));
    const certification = {
      ...currentCertification,
      certification: cert,
      registration: firstCertification.registration,
    };

    if (extensions) {
      getCertificationExtension(certification, extensions);
    }

    return {
      certification,
      showEditModal: defaultTo(false, path(['showEditModal'], prevState)),
      showPaymentModal: defaultTo(false, path(['showPaymentModal'], prevState)),
    };
  }

  renderInfoRow = (name, value) => {
    if (!value) return <noscript />;

    return (
      <p>
        <strong>{name}:</strong> {value}
      </p>
    );
  };

  render() {
    const { creditTypes, match, user, updateUser, updateUserPayments, certificationPayments } = this.props;
    const { certification, showEditModal, showPaymentModal, showReinstatementModal } = this.state;
    const extension = certification.extension;
    const registeredOn = path(['registration', 'createdAt'], certification);
    const deadline = path(['registration', 'deadline'], certification);
    const graduatedOn = path(['registration', 'graduatedOn'], certification);
    const paidPayments = filter((payment) => payment.transactionId != null, certificationPayments);
    const highestPaidYear = compose(
      defaultTo(path(['extra', 'due'], last(certificationPayments)) - 1),
      path(['extra', 'due']),
      last(),
    )(paidPayments);

    // TODO: Make less hardcoded
    const isAfccCertification = certification.certificationId == 4;

    return (
      <Fragment>
        <Card className="mb-4">
          <Card.Header>
            Certification Information
            {match.params.userId && certification.status == magic.certificateRevoked && (
              <button
                className="btn btn-primary btn-sm float-right ml-3"
                onClick={() => this.setState({ showReinstatementModal: true })}
              >
                Reinstate
              </button>
            )}
            {match.params.userId && (
              <button
                className="btn btn-primary btn-sm float-right"
                onClick={() => this.setState({ showEditModal: true })}
              >
                Edit
              </button>
            )}
          </Card.Header>
          <Card.Body>
            {this.renderInfoRow('Type', he.decode(certification.name))}
            {this.renderInfoRow('Status', certification.status)}
            {this.renderInfoRow(
              'Extension',
              extension && extension.approved != null && (
                <Link to={routes.admin.extensions.reviewBuilder(extension.id)}>
                  {extension.approved ? 'Approved' : 'Rejected'}
                </Link>
              ),
            )}
            {this.renderInfoRow(
              'Certificate',
              certification.accredibleUrl && <a href={certification.accredibleUrl}>View</a>,
            )}
            {this.renderInfoRow('Registered On', registeredOn && moment(registeredOn).format('MM/DD/YYYY'))}
            {this.renderInfoRow('Registration Deadline', registeredOn && moment(deadline).format('MM/DD/YYYY'))}
            {this.renderInfoRow('Graduated On', registeredOn && moment(graduatedOn).format('MM/DD/YYYY'))}
          </Card.Body>
        </Card>
        {certification.certification.reportable == true && <CEUListCard creditTypes={creditTypes} />}
        <Card className="mb-4">
          <Card.Header>
            Certification Payments
            {match.params.userId && (
              <button
                className="btn btn-primary btn-sm float-right"
                onClick={() => this.setState({ showPaymentModal: true })}
              >
                Add
              </button>
            )}
          </Card.Header>
          <Card.Body>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paidPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="w-100">{path(['extra', 'due'], payment)}</td>
                    <td className="whiteSpaceNoWrap d-flex align-items-center">
                      <FinalPrice
                        lineItem={payment}
                        productless
                        readonly
                        hidePrice={!match.params.userId && isAfccCertification}
                      />
                      {match.params.userId && (
                        <button
                          className="btn btn-link btn-sm"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this payment?')) {
                              apiDeleteCertificationPayment(match.params.userId, payment.id)
                                .then(({ data }) => updateUserPayments(data))
                                .then(() => handleSuccessBase())
                                .catch((e) => console.log(e));
                            }
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Body>
        </Card>
        <UserCertificationModal
          userCertification={certification}
          updateCertifications={(certifications) => updateUser({ ...user, userCertifications: certifications })}
          show={showEditModal}
          onHide={() => this.setState({ showEditModal: false })}
        />
        <CertificationPaymentModal
          highestPaidYear={highestPaidYear}
          userCertification={certification}
          updatePayments={(payments) => updateUser({ ...user, certificationPayments: payments })}
          show={showPaymentModal}
          onHide={() => this.setState({ showPaymentModal: false })}
        />
        <ReinstateCertificationModal
          userCertification={certification}
          updateCertifications={(certifications) => updateUser({ ...user, userCertifications: certifications })}
          show={showReinstatementModal}
          onHide={() => this.setState({ showReinstatementModal: false })}
        />
      </Fragment>
    );
  }
}

export default withRouter(connect(mapStore)(Certification));
